import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Progress from '../Progress';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import { useNavigate } from 'react-router-dom';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

function ApiKeysTable({ reloadKey }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '', onConfirm: () => {} });
  const navigate = useNavigate();

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/company-master/api-keys`
      );
      const data = response.data?.data || [];
      setKeys(data);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to fetch API keys',
          type: 'error',
        });
      }
    }
    setLoading(false);
  };

  const revokeKey = async (key) => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/company-master/api-keys/${key.id}/revoke`
      );
      setNotify({
        isOpen: true,
        message: response.data?.message || 'API key revoked successfully',
        type: 'success',
      });
      fetchKeys();
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to revoke API key',
          type: 'error',
        });
      }
    }
  };

  const handleRevokeClick = (key) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke API key?',
      subTitle: `Key "${key.name || key.key_prefix}" will stop working immediately. This cannot be undone.`,
      onConfirm: () => revokeKey(key),
    });
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (loading) {
    return <Progress />;
  }

  return (
    <>
      <div className="table-parent">
        <table className="table big-table">
          <thead className="table-dark">
            <tr>
              <th scope="col" className="table-dark">Actions</th>
              <th scope="col">Name</th>
              <th scope="col">Prefix</th>
              <th scope="col">Status</th>
              <th scope="col">Created By</th>
              <th scope="col">Created At</th>
              <th scope="col">Last Used</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center' }}>
                  No API keys yet. Generate one for Finance Console.
                </td>
              </tr>
            )}
            {keys.map((key) => (
              <tr key={key.id}>
                <td>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={key.status !== 'active'}
                    onClick={() => handleRevokeClick(key)}
                  >
                    Revoke
                  </button>
                </td>
                <td>{key.name || '-'}</td>
                <td>{key.key_prefix}…</td>
                <td>{key.status === 'active' ? 'Active' : 'Revoked'}</td>
                <td>{key.created_by || '-'}</td>
                <td>{formatDate(key.created_at)}</td>
                <td>{formatDate(key.last_used_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
}

export default ApiKeysTable;
